import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Heart, BookOpen, X, Flame, Check, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBooks } from "@/hooks/useBooks";
import { useUserBooks } from "@/hooks/useUserBooks";
import { useMyReactionTallies } from "@/hooks/useReactions";
import { buildBookDna } from "@/lib/bookDna";
import { explainMatch } from "@/lib/matchReasons";
import {
  pickDailyMatch,
  msUntilTomorrow,
  formatCountdown,
  getDailyMatchDateKey,
} from "@/lib/dailyMatch";
import type { ReaderSignals } from "@/lib/recommendations";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const SpiceDots = ({ level }: { level: number }) => (
  <span className="inline-flex gap-0.5 align-middle">
    {[1, 2, 3].map((i) => (
      <Flame
        key={i}
        className={`w-3.5 h-3.5 ${i <= level ? "text-primary fill-primary" : "text-muted-foreground/30"}`}
      />
    ))}
  </span>
);

const DailyMatchCard = () => {
  const { user } = useAuth();
  const { books } = useBooks();
  const { items, setStatus, refresh } = useUserBooks();
  const reactionTallies = useMyReactionTallies();

  const [prefs, setPrefs] = useState<{
    favorite_tropes: string[];
    favorite_subgenres: string[];
    emotional_preference: string | null;
    spice_preference: number | null;
    daily_match_email_opt_in?: boolean;
  } | null>(null);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [countdown, setCountdown] = useState(formatCountdown(msUntilTomorrow()));
  const [viewedKey, setViewedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const [{ data: prof }, { data: dis }] = await Promise.all([
        supabase
          .from("profiles")
          .select(
            "favorite_tropes, favorite_subgenres, emotional_preference, spice_preference, daily_match_email_opt_in" as any,
          )
          .eq("id", user.id)
          .maybeSingle(),
        supabase.from("book_dismissals").select("book_id").eq("user_id", user.id),
      ]);
      if (!active) return;
      setPrefs({
        favorite_tropes: (prof as any)?.favorite_tropes ?? [],
        favorite_subgenres: (prof as any)?.favorite_subgenres ?? [],
        emotional_preference: (prof as any)?.emotional_preference ?? null,
        spice_preference: (prof as any)?.spice_preference ?? null,
        daily_match_email_opt_in: (prof as any)?.daily_match_email_opt_in ?? false,
      });
      setDismissedIds((dis ?? []).map((d) => d.book_id));
    })();
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    const id = setInterval(() => setCountdown(formatCountdown(msUntilTomorrow())), 60_000);
    return () => clearInterval(id);
  }, []);

  const signals: ReaderSignals = useMemo(
    () => ({
      favoriteTropes: prefs?.favorite_tropes ?? [],
      favoriteSubgenres: prefs?.favorite_subgenres ?? [],
      emotionalPreference: prefs?.emotional_preference ?? null,
      spicePreference: prefs?.spice_preference ?? null,
      savedBookIds: items.filter((i) => i.status === "tbr").map((i) => i.book_id),
      readBookIds: items.filter((i) => i.status === "read").map((i) => i.book_id),
      dismissedBookIds: dismissedIds,
    }),
    [prefs, items, dismissedIds],
  );

  const dnaSlices = useMemo(() => {
    const ids = new Set([...signals.savedBookIds, ...signals.readBookIds]);
    const affinity = books.filter((b) => ids.has(b.id));
    return buildBookDna(affinity, reactionTallies);
  }, [books, signals.savedBookIds, signals.readBookIds, reactionTallies]);

  const pick = useMemo(() => {
    if (!user || books.length === 0) return null;
    return pickDailyMatch(books, signals, user.id);
  }, [books, signals, user]);

  const explanation = useMemo(() => {
    if (!pick) return null;
    return explainMatch(pick.book, signals, { dnaSlices, reactionTallies });
  }, [pick, signals, dnaSlices, reactionTallies]);

  // Fire daily_match_viewed once per day per user
  useEffect(() => {
    if (!user || !pick) return;
    const key = `dailyMatchViewed:${user.id}:${getDailyMatchDateKey()}`;
    if (viewedKey === key) return;
    if (typeof localStorage !== "undefined" && localStorage.getItem(key)) {
      setViewedKey(key);
      return;
    }
    trackEvent("daily_match_viewed", { book_id: pick.book.id, score: pick.score });
    try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
    setViewedKey(key);
  }, [user, pick, viewedKey]);

  if (!user) return null;
  if (!pick) {
    return (
      <div className="rounded-3xl border border-border bg-card p-6 mb-10">
        <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest font-semibold mb-2">
          <Sparkles className="w-4 h-4" /> Your Daily Match
        </div>
        <p className="text-sm text-muted-foreground">
          Save a few books or take the quiz so we can hand-pick your daily match.
        </p>
      </div>
    );
  }

  const { book, score } = pick;
  const saved = items.some((i) => i.book_id === book.id && i.status === "tbr");

  const handleSave = async () => {
    await setStatus(book.id, "tbr");
    trackEvent("daily_match_saved", { book_id: book.id, score });
  };
  const handleRead = async () => {
    await setStatus(book.id, "read");
    trackEvent("daily_match_clicked", { book_id: book.id, action: "read" });
    refresh();
  };
  const handleSkip = async () => {
    await supabase.from("book_dismissals").insert({ user_id: user.id, book_id: book.id });
    setDismissedIds((p) => [...p, book.id]);
    trackEvent("daily_match_skipped", { book_id: book.id });
    toast.success("New match coming up");
  };

  const toggleEmail = async (next: boolean) => {
    const { error } = await supabase
      .from("profiles")
      .update({ daily_match_email_opt_in: next } as any)
      .eq("id", user.id);
    if (error) return toast.error("Could not save preference");
    setPrefs((p) => (p ? { ...p, daily_match_email_opt_in: next } : p));
    toast.success(next ? "You'll get your Daily Match by email" : "Email reminder off");
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative mb-10 rounded-3xl overflow-hidden border border-primary/30 bg-gradient-to-br from-rose-950/40 via-card to-card shadow-xl"
    >
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-12 w-72 h-72 bg-rose-gold/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative p-5 md:p-7">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
          <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest font-semibold">
            <Sparkles className="w-4 h-4" /> Your Daily Match 💖
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3.5 h-3.5" /> New match in {countdown}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5">
          <Link
            to={`/book/${book.id}`}
            onClick={() => trackEvent("daily_match_clicked", { book_id: book.id, action: "cover" })}
            className={`relative block aspect-[3/4] rounded-2xl bg-gradient-to-br ${book.coverColor} overflow-hidden shadow-lg`}
          >
            {book.coverImageUrl && (
              <img
                src={book.coverImageUrl}
                alt={`${book.title} cover`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute top-2 left-2 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-[10px] font-bold shadow">
              {score}% Match
            </div>
          </Link>

          <div className="min-w-0">
            <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight mb-1">
              <Link
                to={`/book/${book.id}`}
                className="hover:text-primary transition-colors"
                onClick={() => trackEvent("daily_match_clicked", { book_id: book.id, action: "title" })}
              >
                {book.title}
              </Link>
            </h3>
            <p className="text-sm text-muted-foreground mb-3">by {book.author}</p>

            <div className="flex flex-wrap items-center gap-3 text-xs mb-3">
              <span className="inline-flex items-center gap-1"><SpiceDots level={book.spiceLevel} /></span>
              <span className="bg-secondary px-2 py-0.5 rounded-full capitalize">
                {book.emotionalIntensity}
              </span>
              {book.tropes.slice(0, 3).map((t) => (
                <span key={t} className="bg-primary/15 text-primary px-2 py-0.5 rounded-full font-semibold">
                  {t}
                </span>
              ))}
            </div>

            <p className="text-sm italic text-warm border-l-2 border-primary pl-3 mb-4 line-clamp-3">
              "{book.hook}"
            </p>

            {explanation && explanation.reasons.length > 0 && (
              <div className="mb-4">
                <p className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-1.5">
                  Why this matched you
                </p>
                <ul className="space-y-1">
                  {explanation.reasons.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={handleSave} variant={saved ? "secondary" : "default"}>
                <Heart className={`w-3.5 h-3.5 mr-1 ${saved ? "fill-current" : ""}`} />
                {saved ? "Saved" : "Save to TBR"}
              </Button>
              <Button size="sm" variant="outline" onClick={handleRead}>
                <BookOpen className="w-3.5 h-3.5 mr-1" /> Mark Read
              </Button>
              <Button size="sm" variant="ghost" onClick={handleSkip}>
                <X className="w-3.5 h-3.5 mr-1" /> Skip
              </Button>
            </div>

            <label className="mt-4 flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!prefs?.daily_match_email_opt_in}
                onChange={(e) => toggleEmail(e.target.checked)}
                className="accent-primary"
              />
              Email me my Daily Match 💌
            </label>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default DailyMatchCard;
